// Chart utilities and configurations for portal dashboard charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { NUMBER_LOCALE, formatNumber } from "../../utils";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Portal brand colors
export const PORTAL_COLORS = {
  primary: "#722ed1", // Purple primary
  primaryLight: "#d3adf7", // Light purple
  primaryDark: "#531dab", // Dark purple
  success: "#52c41a", // Green
  successLight: "#b7eb8f", // Light green
  error: "#f5222d", // Red
  errorLight: "#ffadd2", // Light red
  warning: "#faad14", // Orange
  info: "#1890ff", // Blue
  text: "#262626", // Dark text
  textSecondary: "#8c8c8c", // Secondary text
  background: "#fafafa", // Light background
  white: "#ffffff",
};

// Create gradient for line charts
export const createGradient = (ctx, color1, color2, direction = "vertical") => {
  const gradient = ctx.createLinearGradient(
    0,
    0,
    direction === "horizontal" ? ctx.canvas.width : 0,
    direction === "horizontal" ? 0 : ctx.canvas.height,
  );
  gradient.addColorStop(0, color1);
  gradient.addColorStop(1, color2);
  return gradient;
};

// Base chart options for consistency
export const getBaseChartOptions = (title = "", type = "line") => ({
  responsive: true,
  maintainAspectRatio: false,
  // The axis numbers like the rest of the homepage (100.000), otherwise Chart.js uses the browser's language
  locale: NUMBER_LOCALE,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12,
          family: "Inter, sans-serif",
          weight: "500",
        },
        color: PORTAL_COLORS.text,
      },
    },
    title: {
      display: !!title,
      text: title,
      font: {
        size: 16,
        family: "Inter, sans-serif",
        weight: "600",
      },
      color: PORTAL_COLORS.text,
      padding: {
        top: 10,
        bottom: 20,
      },
    },
    // Without a family Chart.js falls back to Helvetica/Arial, so every font sets Inter like the rest of the portal
    tooltip: {
      backgroundColor: PORTAL_COLORS.white,
      titleColor: PORTAL_COLORS.text,
      bodyColor: PORTAL_COLORS.text,
      borderColor: PORTAL_COLORS.primary,
      borderWidth: 1,
      cornerRadius: 8,
      padding: 12,
      titleFont: {
        size: 13,
        family: "Inter, sans-serif",
        weight: "600",
      },
      bodyFont: {
        size: 12,
        family: "Inter, sans-serif",
        weight: "500",
      },
      displayColors: true,
      usePointStyle: true,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
        color: "#f0f0f0",
        drawBorder: false,
      },
      ticks: {
        color: PORTAL_COLORS.textSecondary,
        font: {
          size: 11,
          family: "Inter, sans-serif",
        },
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        display: false,
        color: "#f0f0f0",
        drawBorder: false,
      },
      ticks: {
        color: PORTAL_COLORS.textSecondary,
        font: {
          size: 11,
          family: "Inter, sans-serif",
        },
        // Counts, so no 0,5 on the axis when there are only a few
        precision: 0,
      },
    },
  },
  elements: {
    line: {
      tension: 0.4,
      borderWidth: 3,
    },
    point: {
      radius: 0,
      hoverRadius: 6,
      hitRadius: 10,
    },
  },
});

// Timeline chart specific options
export const getTimelineChartOptions = (title = "") => ({
  ...getBaseChartOptions(title),
  scales: {
    ...getBaseChartOptions().scales,
    x: {
      ...getBaseChartOptions().scales.x,
      type: "category",
      title: {
        display: true,
        text: "Time",
        color: PORTAL_COLORS.textSecondary,
        font: {
          size: 12,
          family: "Inter, sans-serif",
          weight: "500",
        },
      },
    },
    y: {
      ...getBaseChartOptions().scales.y,
      title: {
        display: true,
        text: "Error Count",
        color: PORTAL_COLORS.textSecondary,
        font: {
          size: 12,
          family: "Inter, sans-serif",
          weight: "500",
        },
      },
      beginAtZero: true,
      ticks: {
        ...getBaseChartOptions().scales.y.ticks,
        callback: function (value) {
          return formatNumber(value);
        },
      },
    },
  },
});

// Dual-line chart specific options for Tag Monitor
export const getDualLineChartOptions = (title = "") => ({
  ...getBaseChartOptions(title),
  scales: {
    ...getBaseChartOptions().scales,
    x: {
      ...getBaseChartOptions().scales.x,
      type: "category",
      title: {
        display: true,
        text: "Time",
        color: PORTAL_COLORS.textSecondary,
        font: {
          size: 12,
          family: "Inter, sans-serif",
          weight: "500",
        },
      },
    },
    y: {
      ...getBaseChartOptions().scales.y,
      title: {
        display: true,
        text: "Events Count",
        color: PORTAL_COLORS.textSecondary,
        font: {
          size: 12,
          family: "Inter, sans-serif",
          weight: "500",
        },
      },
    },
  },
});

// Format data for DataLayer Guard timeline chart
export const formatDataLayerGuardChartData = (timelineData = []) => {
  if (!Array.isArray(timelineData) || timelineData.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // Extract unique timestamps and sort them
  const timestamps = [
    ...new Set(timelineData.map((item) => item.timestamp)),
  ].sort();

  // Group data by event name
  const eventGroups = timelineData.reduce((acc, item) => {
    const eventName = item.event_name || "Unknown Event";
    if (!acc[eventName]) {
      acc[eventName] = {};
    }
    // Store error_count for each timestamp
    acc[eventName][item.timestamp] = item.error_count || 0;
    return acc;
  }, {});

  // Create datasets with different colors for each event
  const colors = [
    PORTAL_COLORS.primary,
    PORTAL_COLORS.error,
    PORTAL_COLORS.success,
    PORTAL_COLORS.warning,
    PORTAL_COLORS.info,
  ];

  const datasets = Object.keys(eventGroups).map((eventName, index) => {
    const data = timestamps.map(
      (timestamp) => eventGroups[eventName][timestamp] || 0,
    );

    const color = colors[index % colors.length];

    return {
      label: eventName,
      data: data,
      borderColor: color,
      backgroundColor: "transparent",
      fill: false,
      tension: 0.4,
      borderWidth: 3,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 10,
    };
  });

  return {
    labels: timestamps,
    datasets: datasets,
  };
};

// Format data for Tag Monitor dual-line chart
export const formatTagMonitorChartData = (serverData = []) => {
  if (!Array.isArray(serverData) || serverData.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // Extract unique timestamps and sort them
  const timestamps = [
    ...new Set(serverData.map((item) => item.timestamp)),
  ].sort();

  // Separate successful and error events
  const successfulEvents = {};
  const errorEvents = {};

  serverData.forEach((item) => {
    const timestamp = item.timestamp;
    if (!successfulEvents[timestamp]) {
      successfulEvents[timestamp] = 0;
      errorEvents[timestamp] = 0;
    }

    const totalCalls = item.total_calls || 0;
    const totalFails = item.total_fails || 0;
    const successful = totalCalls - totalFails;

    successfulEvents[timestamp] += successful;
    errorEvents[timestamp] += totalFails;
  });

  const datasets = [
    {
      label: "Successful events",
      data: timestamps.map((timestamp) => successfulEvents[timestamp] || 0),
      borderColor: "#CFFF5E",
      backgroundColor: "#CFFF5E20",
      fill: false,
      tension: 0.4,
      borderWidth: 3,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      hidden: true,
    },
    {
      label: "Error events",
      data: timestamps.map((timestamp) => errorEvents[timestamp] || 0),
      borderColor: "#F62064",
      backgroundColor: "#F6206420",
      fill: false,
      tension: 0.4,
      borderWidth: 3,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 10,
    },
  ];

  return {
    labels: timestamps,
    datasets: datasets,
  };
};

// Responsive chart sizing
export const getChartDimensions = (containerWidth) => {
  if (containerWidth < 576) {
    // Mobile
    return { width: containerWidth - 32, height: 250 };
  } else if (containerWidth < 768) {
    // Tablet
    return { width: containerWidth - 48, height: 300 };
  } else {
    // Desktop
    return { width: containerWidth, height: 350 };
  }
};
